import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  claimTask,
  startTask,
  pauseTask,
  resumeTask,
  completeTask,
  rejectTask,
  saveFormData,
  transferTask,
  escalateTask,
  returnTask,
  addTaskNote,
  registerProblem,
} from '../../shared/services/taskService';
import {
  saveFormResponses,
  saveChecklistResponses,
  saveJustificationResponse,
} from '../services/taskResponseService';
import { taskKeys } from '../../shared/keys';
import { invalidateOpsCenter } from '../../../lib/invalidateOpsCenter';
import type { TaskCompletionPayload } from '../../shared/types/task';

interface UseMyTaskMutationsParams {
  organizationId?: string;
  userId?: string;
  userName?: string;
  onSuccess?: () => void;
}

export function useMyTaskMutations(params: UseMyTaskMutationsParams) {
  const { organizationId, userId, userName, onSuccess } = params;
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['minha-mesa'] });
    await queryClient.invalidateQueries({ queryKey: ['execution'] });
    invalidateOpsCenter(queryClient);
    onSuccess?.();
  };

  const invalidateKeepOpen = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['minha-mesa'] });
    await queryClient.invalidateQueries({ queryKey: ['execution'] });
    invalidateOpsCenter(queryClient);
    // Do NOT call onSuccess — keeps cockpit open
  };

  const startMutation = useMutation({
    mutationFn: (taskId: string) => startTask(taskId, userId!),
    onSuccess: invalidateKeepOpen,
  });

  const pauseMutation = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason: string }) => pauseTask(taskId, userId!, reason),
    onSuccess: invalidateKeepOpen,
  });

  const resumeMutation = useMutation({
    mutationFn: (taskId: string) => resumeTask(taskId, userId!),
    onSuccess: invalidateKeepOpen,
  });

  const claimMutation = useMutation({
    mutationFn: (taskId: string) => claimTask({ taskId, userId: userId!, userName }),
    onSuccess: invalidateKeepOpen,
  });

  const completeMutation = useMutation({
    mutationFn: async (payload: TaskCompletionPayload & { instanceId?: string }) => {
      // Require previousUpdatedAt for optimistic locking
      if (!payload.previousUpdatedAt) {
        throw new Error('previousUpdatedAt is required for optimistic locking');
      }
      await completeTask(payload);

      // Persist responses to normalized table
      if (organizationId && payload.instanceId) {
        const baseInput = {
          organizationId,
          taskId: payload.taskId,
          instanceId: payload.instanceId,
          blueprintVersionId: payload.blueprintVersionId,
          respondedBy: payload.completedBy,
          respondedByName: userName,
        };

        const promises: Promise<void>[] = [];

        if (payload.formData && Object.keys(payload.formData).length > 0) {
          promises.push(saveFormResponses({ ...baseInput, formData: payload.formData }));
        }

        if (payload.checklistData && Object.keys(payload.checklistData).length > 0) {
          promises.push(saveChecklistResponses({ ...baseInput, checklistData: payload.checklistData }));
        }

        if (payload.notes) {
          promises.push(saveJustificationResponse({
            ...baseInput,
            justification: payload.notes,
            decision: payload.result === 'approved' ? 'approved' : payload.result === 'rejected' ? 'rejected' : undefined,
          }));
        }

        await Promise.allSettled(promises);
      }
    },
    onSuccess: invalidate,
  });

  const rejectMutation = useMutation({
    mutationFn: async (args: { taskId: string; reason: string; instanceId?: string }) => {
      await rejectTask(args.taskId, userId!, args.reason);

      // Persist rejection justification
      if (organizationId && args.instanceId) {
        await saveJustificationResponse({
          organizationId,
          taskId: args.taskId,
          instanceId: args.instanceId,
          respondedBy: userId!,
          respondedByName: userName,
          justification: args.reason,
          decision: 'rejected',
        }).catch(() => {});
      }
    },
    onSuccess: invalidate,
  });

  const saveFormMutation = useMutation({
    mutationFn: (args: { taskId: string; formData: Record<string, unknown>; checklistData?: Record<string, boolean> }) =>
      saveFormData(args.taskId, args.formData, args.checklistData),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
    },
  });

  const transferMutation = useMutation({
    mutationFn: (args: { taskId: string; newAssigneeId: string; newAssigneeName?: string; justification: string }) =>
      transferTask({ ...args, performedBy: userId! }),
    onSuccess: invalidate,
  });

  const escalateMutation = useMutation({
    mutationFn: (args: { taskId: string; justification: string }) =>
      escalateTask({ ...args, performedBy: userId! }),
    onSuccess: invalidateKeepOpen,
  });

  const returnMutation = useMutation({
    mutationFn: (args: { taskId: string; justification: string }) =>
      returnTask({ ...args, performedBy: userId! }),
    onSuccess: invalidate,
  });

  const addNoteMutation = useMutation({
    mutationFn: (args: { taskId: string; note: string; attachments?: Array<{ name: string; url: string; size?: string }> }) =>
      addTaskNote({ ...args, performedBy: userId! }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.logs(variables.taskId) });
    },
  });

  const registerProblemMutation = useMutation({
    mutationFn: (args: { taskId: string; description: string; problemType: string; impact: string }) =>
      registerProblem({ ...args, performedBy: userId! }),
    onSuccess: invalidateKeepOpen,
  });

  return useMemo(
    () => ({
      claimTask: claimMutation.mutateAsync,
      isClaiming: claimMutation.isPending,

      startTask: startMutation.mutateAsync,
      isStarting: startMutation.isPending,

      pauseTask: pauseMutation.mutateAsync,
      isPausing: pauseMutation.isPending,

      resumeTask: resumeMutation.mutateAsync,
      isResuming: resumeMutation.isPending,

      completeTask: completeMutation.mutateAsync,
      isCompleting: completeMutation.isPending,

      rejectTask: rejectMutation.mutateAsync,
      isRejecting: rejectMutation.isPending,

      saveForm: saveFormMutation.mutateAsync,
      isSavingForm: saveFormMutation.isPending,

      transferTask: transferMutation.mutateAsync,
      isTransferring: transferMutation.isPending,

      escalateTask: escalateMutation.mutateAsync,
      isEscalating: escalateMutation.isPending,

      returnTask: returnMutation.mutateAsync,
      isReturning: returnMutation.isPending,

      addNote: addNoteMutation.mutateAsync,
      isAddingNote: addNoteMutation.isPending,

      registerProblem: registerProblemMutation.mutateAsync,
      isRegisteringProblem: registerProblemMutation.isPending,
    }),
    [
      claimMutation.isPending,
      claimMutation.mutateAsync,
      completeMutation.isPending,
      completeMutation.mutateAsync,
      pauseMutation.isPending,
      pauseMutation.mutateAsync,
      resumeMutation.isPending,
      resumeMutation.mutateAsync,
      rejectMutation.isPending,
      rejectMutation.mutateAsync,
      saveFormMutation.isPending,
      saveFormMutation.mutateAsync,
      startMutation.isPending,
      startMutation.mutateAsync,
      transferMutation.isPending,
      transferMutation.mutateAsync,
      escalateMutation.isPending,
      escalateMutation.mutateAsync,
      returnMutation.isPending,
      returnMutation.mutateAsync,
      addNoteMutation.isPending,
      addNoteMutation.mutateAsync,
      registerProblemMutation.isPending,
      registerProblemMutation.mutateAsync,
    ],
  );
}
